import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Droplets, Plus, Minus } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";

interface WaterTrackerProps {
  date: string;
  targetMl: number;
  onUpdate?: () => void;
}

const GLASS_ML = 250;

export default function WaterTracker({ date, targetMl, onUpdate }: WaterTrackerProps) {
  const [glasses, setGlasses] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const totalGlasses = Math.ceil(targetMl / GLASS_ML);
  const consumedMl = glasses * GLASS_ML;
  const percent = Math.min(Math.round((consumedMl / targetMl) * 100), 100);
  const onTarget = consumedMl >= targetMl;

  const loadWater = useCallback(async () => {
    try {
      const res = await apiFetch("api-diary", { action: "water", date });
      setGlasses(res.data?.glasses || 0);
    } catch (e) {
      console.error("Failed to load water:", e);
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => {
    loadWater();
  }, [loadWater]);

  const saveWater = async (newGlasses: number) => {
    setSaving(true);
    try {
      await apiFetch("api-diary", { action: "water" }, {
        method: "POST",
        body: { date, glasses: newGlasses, target_ml: targetMl },
      });
      setGlasses(newGlasses);
      onUpdate?.();
    } catch (e: any) {
      toast({ title: "Erro ao salvar água", description: e.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const increment = () => saveWater(glasses + 1);
  const decrement = () => {
    if (glasses > 0) saveWater(glasses - 1);
  };

  if (loading) {
    return (
      <div className="bg-card rounded-2xl p-5 shadow-card border border-border animate-pulse">
        <div className="h-5 bg-muted rounded w-32 mb-3" />
        <div className="h-3 bg-muted rounded w-full mb-2" />
        <div className="h-8 bg-muted rounded w-full" />
      </div>
    );
  }

  return (
    <div className="bg-card rounded-2xl p-5 shadow-card border border-border">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-blue-500/10 flex items-center justify-center">
            <Droplets className="w-4 h-4 text-blue-500" />
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Hidratação</p>
            <p className="text-lg font-bold text-foreground">
              {consumedMl} <span className="text-sm font-normal text-muted-foreground">/ {targetMl} ml</span>
            </p>
          </div>
        </div>
        {onTarget && (
          <span className="text-xs font-semibold bg-blue-500/10 text-blue-600 px-2.5 py-1 rounded-full">
            ✓ Meta atingida
          </span>
        )}
      </div>

      {/* Progress bar */}
      <div className="relative mb-4">
        <div className="flex items-center justify-between text-xs text-muted-foreground mb-1.5">
          <span>{glasses} {glasses === 1 ? "copo" : "copos"} (250ml)</span>
          <span className="text-blue-500 font-medium">{percent}%</span>
        </div>
        <div className="w-full h-3 bg-blue-500/10 rounded-full overflow-hidden">
          <motion.div
            className="h-full rounded-full bg-gradient-to-r from-blue-400 to-blue-600"
            initial={{ width: 0 }}
            animate={{ width: `${percent}%` }}
            transition={{ duration: 0.5 }}
          />
        </div>
      </div>

      {/* Glass grid */}
      <div className="flex flex-wrap gap-1.5 mb-4">
        {Array.from({ length: totalGlasses }).map((_, i) => (
          <button
            key={i}
            onClick={() => saveWater(i + 1)}
            disabled={saving}
            className={`w-7 h-7 rounded-lg text-xs font-medium transition-all ${
              i < glasses
                ? "bg-blue-500 text-white shadow-sm"
                : "bg-muted text-muted-foreground hover:bg-blue-500/20"
            }`}
          >
            💧
          </button>
        ))}
      </div>

      {/* Quick buttons */}
      <div className="flex gap-2">
        <button
          onClick={decrement}
          disabled={saving || glasses === 0}
          className="flex items-center justify-center gap-1 flex-1 py-2 rounded-xl bg-muted text-muted-foreground hover:bg-muted/80 transition-colors text-sm font-medium disabled:opacity-40"
        >
          <Minus className="w-3.5 h-3.5" />
          Remover
        </button>
        <button
          onClick={increment}
          disabled={saving}
          className="flex items-center justify-center gap-1 flex-1 py-2 rounded-xl bg-blue-500 text-white hover:bg-blue-600 transition-colors text-sm font-medium disabled:opacity-60"
        >
          <Plus className="w-3.5 h-3.5" />
          Adicionar copo
        </button>
      </div>
    </div>
  );
}
